import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Spinner, Button, Card, CardBody } from 'reactstrap';
import {
  RefreshCw, Filter, Search, Calendar, FileText, Package,
  ChevronLeft, ChevronRight, Download
} from 'react-feather';
import http from '../../../components/network';
import downloadExcel from '../../../utils/downloadExcel';

const Movements = () => {
  const { t } = useTranslation();

  const [exporting, setExporting] = useState(false);

  const [data, setData] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    document_type: '',
    document_number: '',
    start_date: '',
    end_date: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await http.get('/inventory/movements', {
        params: { page, per_page: 25, ...filters },
      });
      setData(response.data.data || []);
      setMeta({
        current_page: response.data.current_page,
        last_page: response.data.last_page,
        total: response.data.total,
      });
    } catch (error) {
      console.error('Error fetching movements:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const formatNumber = (num) => {
    return new Intl.NumberFormat('id-ID').format(parseFloat(num) || 0);
  };

  const handleFilter = () => {
    setPage(1);
    setTimeout(fetchData, 50);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadExcel(
        '/inventory/export/movements',
        {
          document_type: filters.document_type || undefined,
          document_number: filters.document_number || undefined,
          start_date: filters.start_date || undefined,
          end_date: filters.end_date || undefined,
        },
        'stock-movements.xlsx'
      );
    } catch (err) {
      alert('Gagal export Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleReset = () => {
    setFilters({ document_type: '', document_number: '', start_date: '', end_date: '' });
    setPage(1);
    setTimeout(fetchData, 100);
  };

  const getDocTypeStyle = (type) => {
    if (type === 'Purchase Invoice') return { backgroundColor: '#d1fae5', color: '#065f46' };
    if (
      type === 'Pharmacy Prescription' ||
      type === 'Pharmacy Compound' ||
      type === 'Pharmacy Sale'
    ) {
      return { backgroundColor: '#fee2e2', color: '#991b1b' };
    }
    if (type === 'Refund' || type === 'Purchase Return') {
      return { backgroundColor: '#fef3c7', color: '#92400e' };
    }
    return { backgroundColor: '#e5e7eb', color: '#1f2937' };
  };

  const badgeStyle = (type) => ({
    padding: '4px 10px',
    borderRadius: '10px',
    fontSize: '10.5px',
    fontWeight: 600,
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
    display: 'inline-block',
    whiteSpace: 'nowrap',
    ...getDocTypeStyle(type),
  });

  const totalPages = meta?.last_page || 1;
  const currentPage = meta?.current_page || 1;
  const total = meta?.total || 0;
  const startIdx = (currentPage - 1) * 25 + 1;
  const endIdx = Math.min(currentPage * 25, total);

  return (
    <div>
      {/* ═══════ FILTER CARD ═══════ */}
      <Card className="border-0 shadow-sm mb-3">
        <CardBody className="p-3">
          <div className="d-flex align-items-center gap-2 mb-3">
            <div
              className="d-flex align-items-center justify-content-center rounded"
              style={{ width: '32px', height: '32px', backgroundColor: '#eff6ff', color: '#3b82f6' }}
            >
              <Filter size={16} />
            </div>
            <div>
              <h6 className="fw-bold mb-0" style={{ color: '#1e293b' }}>{t('Filter')}</h6>
              <small className="text-muted">{t('Filter stock movements')}</small>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 2fr 2fr 2fr 2fr',
              gap: '12px',
              alignItems: 'end',
            }}
          >
            {/* Doc Type */}
            <div>
              <div
                className="small fw-bold text-muted text-uppercase"
                style={{ fontSize: '10px', letterSpacing: '0.5px', marginBottom: '6px' }}
              >
                <FileText size={12} className="me-1" />
                {t('Doc Type')}
              </div>
              <Input
                type="select"
                value={filters.document_type}
                onChange={(e) => setFilters({ ...filters, document_type: e.target.value })}
                style={{ borderRadius: '8px', fontSize: '13px', height: '38px' }}
              >
                <option value="">{t('All Types')}</option>
                <option value="Purchase Invoice">{t('Purchase Invoice')}</option>
                <option value="Purchase Return">{t('Purchase Return')}</option>
                <option value="Pharmacy Prescription">{t('Pharmacy Prescription')}</option>
                <option value="Pharmacy Compound">{t('Pharmacy Compound')}</option>
                <option value="Pharmacy Sale">{t('Pharmacy Sale')}</option>
              </Input>
            </div>

            {/* Doc Number */}
            <div>
              <div
                className="small fw-bold text-muted text-uppercase"
                style={{ fontSize: '10px', letterSpacing: '0.5px', marginBottom: '6px' }}
              >
                <Search size={12} className="me-1" />
                {t('Doc Number')}
              </div>
              <Input
                type="text"
                placeholder={t('Search number...')}
                value={filters.document_number}
                onChange={(e) => setFilters({ ...filters, document_number: e.target.value })}
                style={{ borderRadius: '8px', fontSize: '13px', height: '38px' }}
              />
            </div>

            {/* Start Date */}
            <div>
              <div
                className="small fw-bold text-muted text-uppercase"
                style={{ fontSize: '10px', letterSpacing: '0.5px', marginBottom: '6px' }}
              >
                <Calendar size={12} className="me-1" />
                {t('From Date')}
              </div>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                style={{ borderRadius: '8px', fontSize: '13px', height: '38px' }}
              />
            </div>

            {/* End Date */}
            <div>
              <div
                className="small fw-bold text-muted text-uppercase"
                style={{ fontSize: '10px', letterSpacing: '0.5px', marginBottom: '6px' }}
              >
                <Calendar size={12} className="me-1" />
                {t('To Date')}
              </div>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                style={{ borderRadius: '8px', fontSize: '13px', height: '38px' }}
              />
            </div>

            {/* Buttons */}
            <div>
              <div style={{ fontSize: '10px', marginBottom: '6px' }}>&nbsp;</div>
              <div className="d-flex gap-2">
                <Button
                  color="primary"
                  onClick={handleFilter}
                  disabled={loading}
                  style={{ borderRadius: '8px', fontWeight: 600, height: '38px', flex: 1 }}
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <RefreshCw size={14} className="me-1" />
                      {t('Filter')}
                    </>
                  )}
                </Button>
                <Button
                  color="light"
                  onClick={handleReset}
                  style={{
                    borderRadius: '8px',
                    height: '38px',
                    border: '1px solid #dee2e6',
                    padding: '0 12px',
                  }}
                  title={t('Reset')}
                >
                  {t('Reset')}
                </Button>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ═══════ TABLE ═══════ */}
      <Card className="border-0 shadow-sm">
        <CardBody className="p-0">
          <div className="p-3 border-bottom d-flex align-items-center justify-content-between">
            <div>
              <h6 className="fw-bold mb-0" style={{ color: '#1e293b' }}>
                {t('Stock Movements')}
              </h6>
              <small className="text-muted">
                {total} {t('transactions')}
              </small>
            </div>
            <Button
              color="success"
              size="sm"
              onClick={handleExport}
              disabled={exporting}
              className="d-flex align-items-center gap-1"
              style={{ borderRadius: '8px', fontWeight: 600 }}
            >
              {exporting ? <Spinner size="sm" /> : <><Download size={14} /> {t('Export Excel')}</>}
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              {/* Header */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '110px 150px 170px 3fr 130px 90px 100px 3fr',
                  gap: 0,
                  background: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: '#6b7280',
                  fontWeight: 700,
                  minWidth: '1250px',
                }}
              >
                <div style={{ padding: '10px 12px' }}>{t('Date')}</div>
                <div style={{ padding: '10px 12px' }}>{t('Doc No')}</div>
                <div style={{ padding: '10px 12px' }}>{t('Type')}</div>
                <div style={{ padding: '10px 12px' }}>{t('Item')}</div>
                <div style={{ padding: '10px 12px' }}>{t('Batch')}</div>
                <div style={{ padding: '10px 12px', textAlign: 'center' }}>{t('Qty')}</div>
                <div style={{ padding: '10px 12px', textAlign: 'right' }}>{t('Balance')}</div>
                <div style={{ padding: '10px 12px' }}>{t('Supplier')}</div>
              </div>

              {/* Body */}
              {data.length === 0 ? (
                <div className="text-center text-muted py-5" style={{ minWidth: '1250px' }}>
                  <Package size={32} style={{ opacity: 0.3 }} className="mb-2" />
                  <div>{t('No movements found')}</div>
                </div>
              ) : (
                data.map((row, idx) => (
                  <div
                    key={row.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '110px 150px 170px 3fr 130px 90px 100px 3fr',
                      gap: 0,
                      borderBottom: '1px solid #f1f5f9',
                      fontSize: '13px',
                      background: idx % 2 === 0 ? '#fff' : '#fafafa',
                      minWidth: '1250px',
                    }}
                  >
                    <div
                      style={{
                        padding: '10px 12px',
                        color: '#6b7280',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {new Date(row.document_date).toLocaleDateString('id-ID', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </div>
                    <div
                      style={{
                        padding: '10px 12px',
                        fontWeight: 600,
                        fontSize: '12px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={row.document_number}
                    >
                      {row.document_number}
                    </div>
                    <div style={{ padding: '10px 12px' }}>
                      <span style={badgeStyle(row.document_type)}>{row.document_type}</span>
                    </div>
                    <div
                      style={{
                        padding: '10px 12px',
                        fontWeight: 600,
                        color: '#1e293b',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={row.item?.item_name || '-'}
                    >
                      {row.item?.item_name || '-'}
                    </div>
                    <div
                      style={{
                        padding: '10px 12px',
                        color: '#6b7280',
                        fontSize: '12px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={row.batch_number || '-'}
                    >
                      {row.batch_number || '-'}
                    </div>
                    <div
                      style={{
                        padding: '10px 12px',
                        textAlign: 'center',
                        fontWeight: 700,
                        color: row.actual_qty > 0 ? '#10b981' : '#ef4444',
                      }}
                    >
                      {row.actual_qty > 0 ? '+' : ''}
                      {formatNumber(row.actual_qty)}
                    </div>
                    <div
                      style={{
                        padding: '10px 12px',
                        textAlign: 'right',
                        fontWeight: 700,
                        color: '#1e293b',
                      }}
                    >
                      {formatNumber(row.remaining_qty)}
                    </div>
                    <div
                      style={{
                        padding: '10px 12px',
                        color: '#6b7280',
                        fontSize: '12px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={row.supplier?.supplier_name || '-'}
                    >
                      {row.supplier?.supplier_name || '-'}
                    </div>
                  </div>
                ))
              )}

              {/* ═══════ PAGINATION ═══════ */}
              {total > 0 && (
                <div
                  className="d-flex justify-content-between align-items-center p-3"
                  style={{
                    borderTop: '1px solid #e2e8f0',
                    minWidth: '1250px',
                    background: '#fff',
                  }}
                >
                  <small className="text-muted">
                    {t('Showing')} <strong>{startIdx}</strong>–<strong>{endIdx}</strong> {t('of')} <strong>{total}</strong> {t('items')}
                  </small>

                  <div className="d-flex align-items-center gap-2">
                    <Button
                      size="sm"
                      color="light"
                      className="border d-flex align-items-center"
                      disabled={currentPage === 1}
                      onClick={() => setPage(currentPage - 1)}
                      style={{ borderRadius: '6px' }}
                    >
                      <ChevronLeft size={14} />
                    </Button>

                    <span className="text-muted small px-2">
                      {t('Page')} <strong>{currentPage}</strong> {t('of')} <strong>{totalPages}</strong>
                    </span>

                    <Button
                      size="sm"
                      color="light"
                      className="border d-flex align-items-center"
                      disabled={currentPage === totalPages}
                      onClick={() => setPage(currentPage + 1)}
                      style={{ borderRadius: '6px' }}
                    >
                      <ChevronRight size={14} />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default Movements;